import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { NgChartsModule } from 'ng2-charts';
import { ChartData, ChartConfiguration, ChartType } from 'chart.js';
import { ChartUtilities } from '@app/shared/utils/chartData';

@Component({
  selector: 'app-chart',
  standalone: true,
  imports: [CommonModule, NgChartsModule],
  template: `
    <div class="chart-wrapper">
      <canvas baseChart
        class="universal-chart"
        [data]="chartData"
        [options]="chartOptions"
        [type]="chartType"
        [style.width]="widthStyle"
        [style.height.px]="height"
        [attr.aria-label]="ariaLabel || null">
      </canvas>
      <div *ngIf="displayLegend" class="chart-legend">
        <div class="legend-item" *ngFor="let item of legendItems">
          <span class="legend-swatch" [style.background]="item.color"></span>
          <span class="legend-label">{{ item.label }}</span>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host { display:block; }
    .universal-chart { display:block; width: 100%; height: 100%; }
    .chart-wrapper { position:relative; display:block; width: 100%; height: 100%; }
    .chart-legend { display:flex; flex-wrap:wrap; gap:8px; margin-top:6px; font-size:11px; line-height:1.2; }
    .legend-item { display:flex; align-items:center; gap:4px; }
    .legend-swatch { width:12px; height:12px; border-radius:2px; box-shadow:0 0 0 1px rgba(0,0,0,0.08); }
    .legend-label { user-select:none; }
  `]
})
export class GraphComponent implements OnChanges {
  @Input() data: any[] = [];
  @Input() type: 'bar' | 'line' | 'area' | 'pie' | 'doughnut' = 'bar';
  @Input() xKey = '';
  @Input() yKeys: string[] = [];
  @Input() labelKey = '';
  @Input() valueKey = '';
  @Input() width: number | string = '100%';
  @Input() height = 220;
  @Input() ariaLabel = 'graph';
  @Input() debug = false;
  @Input() showLegend = false;
  @Input() colorMap: Record<string,string> = {};
  @Input() showCalloutLabels = false;
  @Input() showSectorLabels = false;
  @Input() applyColorMap = true;

  // ng2-charts properties
  chartData: ChartData<any> = { labels: [], datasets: [] };
  chartOptions: ChartConfiguration['options'] = {};
  chartType: ChartType = 'bar';
  legendItems: Array<{label: string, color: string}> = [];

  get displayLegend(): boolean { return this.showLegend; }
  get widthStyle(): string { return typeof this.width === 'number' ? `${this.width}px` : (this.width || '100%'); }

  ngOnChanges(changes: SimpleChanges): void {
    this.buildChartData();
  }

  private buildChartData(): void {
    const fallbackData = [
      { category: 'A', value: 5 },
      { category: 'B', value: 7 },
      { category: 'C', value: 3 }
    ];

    const workingData = this.data && this.data.length > 0 ? this.data : fallbackData;

    // Set chart type, handling donut -> doughnut mapping
    this.chartType = this.type as ChartType;

    if (this.type === 'pie' || this.type === 'doughnut') {
      this.buildPieChart(workingData);
    } else {
      this.buildCartesianChart(workingData);
    }

    this.buildLegendItems();
    this.buildChartOptions();

    if (this.debug) {
      console.log('[GraphComponent] Built chart:', {
        type: this.chartType,
        data: this.chartData,
        options: this.chartOptions
      });
    }
  }

  private buildPieChart(data: any[]): void {
    const labels: string[] = [];
    const values: number[] = [];
    const colors: string[] = [];

    data.forEach((item, index) => {
      const label = this.labelKey ? item[this.labelKey] : item.category || `Item ${index + 1}`;
      const value = this.valueKey ? item[this.valueKey] : item.value || 0;

      labels.push(label);
      values.push(value);

      // Apply color mapping
      const colorKey = label.toLowerCase();
      const mappedColor = this.colorMap[colorKey];
      colors.push(mappedColor || this.getColorForKey(colorKey, index, ChartUtilities.getChartColors()));
    });

    this.chartData = {
      labels,
      datasets: [{
        data: values,
        backgroundColor: colors,
        borderColor: colors.map(c => c),
        borderWidth: 2
      }]
    };
  }

  private buildCartesianChart(data: any[]): void {
    const labels: string[] = [];
    const datasets: any[] = [];

    // Extract labels from xKey
    if (this.xKey) {
      labels.push(...data.map(item => item[this.xKey]));
    } else {
      labels.push(...data.map((item, index) => `Item ${index + 1}`));
    }

    // Build datasets from yKeys
    if (this.yKeys && this.yKeys.length > 0) {
      this.yKeys.forEach((yKey, index) => {
        const values = data.map(item => item[yKey] || 0);

        // For bar charts with colorMap, create individual colors for each bar based on x-axis labels
        let colors: string | string[];
        if (this.type === 'bar' && this.colorMap && Object.keys(this.colorMap).length > 0 && labels.length > 0) {
          // Map colors to each bar based on the x-axis label (vendor name)
          colors = labels.map((label, labelIndex) => {
            const labelKey = (typeof label === 'string' ? label : String(label)).toLowerCase();
            const mappedColor = this.colorMap[labelKey];
            return mappedColor || this.getColorForKey(yKey, labelIndex, ChartUtilities.getChartColors());
          });
        } else {
          colors = this.getColorForKey(yKey, index, ChartUtilities.getChartColors());
        }

        const dataset: any = {
          label: yKey,
          data: values,
          borderColor: colors,
          backgroundColor: this.type === 'line' ? 'transparent' : colors,
          fill: this.type === 'area',
          tension: this.type === 'line' ? 0.4 : undefined,
          borderWidth: 2
        };

        datasets.push(dataset);
      });
    }

    this.chartData = { labels, datasets };
  }

  private buildChartOptions(): void {
    const baseOptions: ChartConfiguration['options'] = {
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 0 // Disable animations
      },
      plugins: {
        legend: {
          display: this.showLegend,
          position: 'top'
        }
      }
    };

    if (this.type === 'pie' || this.type === 'doughnut') {
      this.chartOptions = {
        ...baseOptions,
        ...(this.type === 'doughnut' && { cutout: '65%' }), // Reduce doughnut inner radius
        radius: '100%', // Reduce overall pie/doughnut radius
        plugins: {
          ...baseOptions.plugins,
          datalabels: {
            display: false // Hide data labels on the chart
          }
        },
        elements: {
          arc: {
            borderWidth: 2
          }
        },
        layout: {
          padding: 0
        }
      } as any; // Type assertion to allow radius property
    } else {
      this.chartOptions = {
        ...baseOptions,
        plugins: {
          ...baseOptions.plugins,
          datalabels: {
            display: false // Hide data labels on bars
          }
        } as any,
        scales: {
          x: {
            display: true // Hide x-axis
          },
          y: {
            display: true,
            beginAtZero: true,
            title: {
              display: true,
              text: 'Values'
            }
          }
        }
      };
    }
  }

  private buildLegendItems(): void {
    this.legendItems = [];

    if (this.chartData.datasets) {
      this.chartData.datasets.forEach((dataset: any, index: number) => {
        if (this.type === 'pie' || this.type === 'doughnut') {
          // For pie/doughnut charts, create legend items from labels
          this.chartData.labels?.forEach((label: any, labelIndex: number) => {
            this.legendItems.push({
              label: label as string,
              color: Array.isArray(dataset.backgroundColor)
                ? dataset.backgroundColor[labelIndex]
                : dataset.backgroundColor
            });
          });
        } else {
          // For other charts, create legend items from datasets
          this.legendItems.push({
            label: dataset.label || `Series ${index + 1}`,
            color: dataset.borderColor || dataset.backgroundColor
          });
        }
      });
    }
  }


  // Map yKey names to brand colors; fallback to palette order.
  private getColorForKey(yKey: string, idx: number, palette: string[]): string {
    if (!yKey) return palette[idx % palette.length];
    const key = yKey.toLowerCase();

    // Apply color mapping if available
    const mappedColor = this.colorMap[key];
    if (mappedColor) return mappedColor;

    // Brand-specific colors
    const brandMap: Record<string,string> = {
      available: '#4CAF50',
      allocated: '#2196F3',
      'under repair': '#FF9800',
      active: '#4CAF50',
      'in transit': '#2196F3',
      maintenance: '#FF9800',
      retired: '#9E9E9E',
      'lost/damaged': '#F44336'
    };

    return brandMap[key] || palette[idx % palette.length];
  }
}
